import Vue from 'vue';
import VueRouter from 'vue-router';
import { isStoreApp, STORE_PURCHASE_ROUTE_NAMES } from '../utils/platform';
import store from '../store';

Vue.use(VueRouter);

/**
 * The `/:clientSlug` shell matches any first path segment, including "login" and
 * "enroll". Its default child then redirects to client-login, producing /login/login
 * or /enroll/login. Reserved single-segment URLs must use the real routes instead.
 */
function resolveReservedTopLevelClientSlug(to, from, next) {
  const slug = to.params.clientSlug;
  if (!slug) {
    next();
    return;
  }
  const parts = to.path.split('/').filter(Boolean);
  if (parts.length !== 1 || parts[0] !== slug) {
    next();
    return;
  }
  const lower = slug.toLowerCase();
  if (lower === 'login') {
    next({ name: 'login', replace: true });
    return;
  }
  if (lower === 'enroll' || lower === 'register') {
    next({ name: 'enroll', replace: true });
    return;
  }
  if (lower === 'password_reset') {
    next({ name: 'password_reset', replace: true });
    return;
  }
  next();
}

function scrollToTop() {
  window.scrollTo(0, 0);
  const resetMain = () => {
    const main = document.querySelector('main');
    if (main) main.scrollTop = 0;
  };
  Vue.nextTick(resetMain);
  setTimeout(resetMain, 100);
}

const routes = [
  {
    path: '/',
    name: 'home',
    redirect: { name: 'login' },
    meta: { allowAnonymous: true, title: 'To Be Legacy' },
  },
  {
    path: '/',
    component: () => import('../components/RouterViewWrapper.vue'),
    props: true,
    children: [
      {
        path: 'profile',
        component: () => import('../views/Profile.vue'),
        props: true,
        children: [
          {
            path: '',
            name: 'main',
            props: true,
            meta: { title: 'Dashboard' },
            component: () => import('../views/Main.vue'),
          },
          {
            path: 'welcome/:version?',
            name: 'welcome',
            props: true,
            meta: { title: 'Welcome' },
            component: () => import('../views/users/Welcome.vue'),
          },
          {
            path: 'program/living_in_excellence',
            name: 'living_in_excellence',
            props: true,
            meta: { title: 'Living In Excellence' },
            component: () => import('../views/programs/LivingInExcellence.vue'),
          },
          {
            path: 'enroll-new',
            name: 'enroll_new',
            meta: { title: 'Enroll' },
            component: () => import('../views/users/EnrollNew.vue'),
          },
          {
            path: 'membership',
            name: 'ios_membership',
            meta: { title: 'Existing Members' },
            component: () => import('../views/users/IosMembership.vue'),
          },
          {
            path: 'program/:id/cancel',
            name: 'program_cancel',
            props: true,
            meta: { title: 'Cancel Program' },
            component: () => import('../views/programs/Cancel.vue'),
          },
          {
            path: 'edit_account',
            name: 'edit_account',
            meta: { title: 'Edit Account' },
            component: () => import('../views/users/EditAccount.vue'),
          },
          {
            path: 'manage-programs',
            name: 'manage_programs',
            meta: { title: 'Manage Programs' },
            component: () => import('../views/users/ManagePrograms.vue'),
          },
          {
            path: 'program/:id/update_payment_method',
            name: 'update_payment_method',
            props: true,
            meta: { title: 'Update Payment Method' },
            component: () => import('../views/users/UpdatePaymentMethod.vue'),
          },
          {
            path: 'journals',
            name: 'journals',
            meta: { title: 'Journals' },
            component: () => import('../views/programs/Journals.vue'),
          },
          {
            path: 'ask-a-coach',
            name: 'ask_a_coach',
            meta: { title: 'Ask a Coach' },
            component: () => import('../views/AskACoach.vue'),
          },
          {
            path: 'store-and-media',
            name: 'store_and_media',
            meta: { title: 'Store and Media' },
            component: () => import('../views/StoreAndMedia.vue'),
          },
          {
            path: 'page/:slug',
            name: 'flatpage',
            props: true,
            meta: { title: 'Page' },
            component: () => import('../views/Flatpage.vue'),
          },
          {
            path: 'notifications',
            name: 'notifications',
            meta: { title: 'Notifications' },
            component: () => import('../views/users/Notifications.vue'),
          },
          {
            path: 'delete_account',
            name: 'delete_account',
            meta: { title: 'Delete Account' },
            component: () => import('../views/users/DeleteAccount.vue'),
          },
          {
            path: 'logout',
            name: 'logout',
            meta: { title: 'Logout' },
            component: () => import('../views/users/Logout.vue'),
          },
        ],
      },
      {
        path: 'program/:id',
        name: 'program',
        props: true,
        meta: { title: 'Program' },
        component: () => import('../views/programs/Program.vue'),
      },
    ],
  },
  {
    path: '/login',
    name: 'login',
    meta: { allowAnonymous: true, title: 'Login' },
    component: () => import('../views/users/Login.vue'),
    props: route => ({
      clientSlug: route.params.client_slug || process.env.VUE_APP_CLIENT_SLUG,
    }),
  },
  {
    path: '/:client_slug/login',
    name: 'client-login',
    meta: { allowAnonymous: true, title: 'Login' },
    component: () => import('../views/users/Login.vue'),
    props: route => ({
      clientSlug: route.params.client_slug,
    }),
  },
  {
    path: '/enroll',
    name: 'enroll',
    meta: { allowAnonymous: true, title: 'Enroll' },
    component: () => import('../views/users/Enroll.vue'),
  },
  {
    path: '/membership',
    name: 'ios_membership_public',
    meta: { allowAnonymous: true, title: 'Existing Members' },
    component: () => import('../views/users/IosMembership.vue'),
  },
  {
    path: '/:client_slug/enroll',
    name: 'client-enroll',
    meta: { allowAnonymous: true, title: 'Enroll' },
    component: () => import('../views/users/Enroll.vue'),
    props: true,
  },
  {
    path: '/:clientSlug',
    beforeEnter: resolveReservedTopLevelClientSlug,
    component: () => import('../components/RouterViewWrapper.vue'),
    props: true,
    meta: { allowAnonymous: true },
    children: [
      {
        path: '',
        redirect: to => ({ name: 'client-login', params: { client_slug: to.params.clientSlug } }),
      },
      {
        path: 'profile',
        component: () => import('../views/Profile.vue'),
        props: true,
        children: [
          {
            path: '',
            name: 'client-main',
            props: true,
            meta: { title: 'Dashboard' },
            component: () => import('../views/Main.vue'),
          },
          {
            path: 'welcome/:version?',
            name: 'client-welcome',
            props: true,
            meta: { title: 'Welcome' },
            component: () => import('../views/users/Welcome.vue'),
          },
          {
            path: 'program/living_in_excellence',
            name: 'client-living_in_excellence',
            props: true,
            meta: { title: 'Living In Excellence' },
            component: () => import('../views/programs/LivingInExcellence.vue'),
          },
          {
            path: 'enroll-new',
            name: 'client-enroll_new',
            meta: { title: 'Enroll' },
            component: () => import('../views/users/EnrollNew.vue'),
            props: true,
          },
          {
            path: 'membership',
            name: 'client-ios_membership',
            meta: { title: 'Existing Members' },
            component: () => import('../views/users/IosMembership.vue'),
          },
          {
            path: 'program/:id/cancel',
            name: 'client-program_cancel',
            props: true,
            meta: { title: 'Cancel Program' },
            component: () => import('../views/programs/Cancel.vue'),
          },
          {
            path: 'edit_account',
            name: 'client-edit_account',
            meta: { title: 'Edit Account' },
            component: () => import('../views/users/EditAccount.vue'),
          },
          {
            path: 'manage-programs',
            name: 'client-manage_programs',
            meta: { title: 'Manage Programs' },
            component: () => import('../views/users/ManagePrograms.vue'),
          },
          {
            path: 'program/:id/update_payment_method',
            name: 'client-update_payment_method',
            props: true,
            meta: { title: 'Update Payment Method' },
            component: () => import('../views/users/UpdatePaymentMethod.vue'),
          },
          {
            path: 'journals',
            name: 'client-journals',
            meta: { title: 'Journals' },
            component: () => import('../views/programs/Journals.vue'),
          },
          {
            path: 'ask-a-coach',
            name: 'client-ask_a_coach',
            meta: { title: 'Ask a Coach' },
            component: () => import('../views/AskACoach.vue'),
          },
          {
            path: 'store-and-media',
            name: 'client-store_and_media',
            meta: { title: 'Store and Media' },
            component: () => import('../views/StoreAndMedia.vue'),
          },
          {
            path: 'page/:slug',
            name: 'client-flatpage',
            props: true,
            meta: { title: 'Page' },
            component: () => import('../views/Flatpage.vue'),
          },
          {
            path: 'notifications',
            name: 'client-notifications',
            meta: { title: 'Notifications' },
            component: () => import('../views/users/Notifications.vue'),
          },
          {
            path: 'delete_account',
            name: 'client-delete_account',
            meta: { title: 'Delete Account' },
            component: () => import('../views/users/DeleteAccount.vue'),
          },
          {
            path: 'logout',
            name: 'client-logout',
            meta: { title: 'Logout' },
            component: () => import('../views/users/Logout.vue'),
          },
        ],
      },
      {
        path: 'program/:id',
        name: 'client-program',
        props: true,
        meta: { title: 'Program' },
        component: () => import('../views/programs/Program.vue'),
      },
    ],
  },
  {
    path: '/password_reset',
    name: 'password_reset',
    meta: { allowAnonymous: true, title: 'Password Reset' },
    component: () => import('../views/users/PasswordReset.vue'),
  },
  {
    path: '/password_reset/:uidb64/:token/',
    name: 'password_reset_confirm',
    meta: { allowAnonymous: true, title: 'Password Reset Confirm' },
    props: true,
    component: () => import('../views/users/PasswordResetConfirm.vue'),
  },
  {
    path: '/register',
    redirect: { name: 'enroll' },
  },
  {
    path: '*',
    name: 'page_not_found',
    meta: { allowAnonymous: true, title: 'Page Not Found' },
    component: () => import('../views/PageNotFound.vue'),
  },
];

const router = new VueRouter({
  mode: 'history',
  base: process.env.NODE_ENV === 'production' && !process.env.CAPACITOR_BUILD ? '/app/' : '/',
  routes,
  scrollBehavior() {
    return { x: 0, y: 0 };
  },
});

router.afterEach(() => {
  scrollToTop();
});

router.beforeEach((to, from, next) => {
  document.title = to.meta.title || 'To Be Legacy';
  store.commit('latestFromRoute', from);

  if (isStoreApp() && STORE_PURCHASE_ROUTE_NAMES.includes(to.name)) {
    if (store.state.userData) {
      next({ name: 'ios_membership', replace: true });
    } else {
      next({ name: 'ios_membership_public', replace: true });
    }
    return;
  }

  if (to.matched.some(record => record.meta.allowAnonymous)) {
    next();
  } else if (store.state.userData === null) {
    // For Capacitor/mobile builds, always allow general login
    if (process.env.CAPACITOR_BUILD) {
      next();
    } else {
      // Allow access to protected routes - login component handles client logic
      next();
    }
  } else {
    next();
  }
});

export default router;
